import {useEffect,useState} from 'react';
import moment from 'moment';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CustomSelect from '../common/CustomSelect';
import DateField from '../common/DateField';
import {
  useCreateDataExportMutation,
  useGetDataExportsQuery,
} from '../../redux/services/dataExportApi';

const handleClick = ({
  post, 
  setGenerating,
  setDownloadReady,
  setExportId,
}) => (exportType, {startDate, endDate} = {}) => async e => {
  setGenerating(true);
  setDownloadReady(false);
  let body = {
    export_type: exportType,
  };
  if (exportType === 'sessions' && startDate) {
    body.start_date = startDate;
    body.end_date = endDate;
  }
  const req = await post(body);
  if (req.error) setGenerating(false);
  setExportId(req.data.id);
};

const DataExportScreen = props => {
  const [generating, setGenerating] = useState(false);
  const [downloadReady, setDownloadReady] = useState(false);
  const [exportId, setExportId] = useState(null);
  const [post, response] = useCreateDataExportMutation();
  const [exportType, setExportType] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const onClick = handleClick({post, setDownloadReady, setGenerating, setExportId});
  const success = downloadReady;
  const dataExport = useGetDataExportsQuery(
    {id: exportId}, 
    {skip: !generating || !exportId, pollingInterval: 1000}
  );
  const downloadUrl = dataExport.data?.data?.[0]?.url;
  useEffect(() => {
    if (downloadUrl) {
      setGenerating(false);
      setDownloadReady(true);
    }
  }, [downloadUrl]);
  return (
    <Box>
      {response.isError && 
        <Alert severity='error'>
          {response.error?.data?.errors?.[0]}
        </Alert>
      }
      {
        generating &&
        <Stack spacing={1} padding={4} justifyContent='center' alignItems='center'>
          <CircularProgress />
          <Typography>
            Generating export... This could take a few minutes. Please do not leave this page.
          </Typography>
        </Stack>
      }
      {
        success && 
          <Alert severity='success'>
            Created export!
          </Alert>
      }
      <Stack spacing={1} padding={4} justifyContent='center'>
        <CustomSelect
          label='Export Type'
          onChange={ e => {
            setGenerating(false);
            setDownloadReady(false);
            setExportId(null);
            setExportType(e.target.value)
          }}
          options={[
            {label: 'Sessions', value: 'sessions'},
            {label: 'Patients', value: 'patients'},
            {label: 'Providers', value: 'providers'},
          ]}
        />
        {exportType === 'sessions' &&
          <>
            <Grid container spacing={2}>
              <Grid item  xs={12} md={6}>
                <DateField
                  label='Start Date'
                  maxDate={moment(endDate)}
                  value={moment(startDate)}
                  onChange={e => {
                    setStartDate(moment(e.target.value).startOf('day').toISOString())
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateField
                  label='End Date'
                  disabled={!startDate}
                  value={moment(endDate)}
                  minDate={startDate}
                  onChange={e => {
                    setEndDate(moment(e.target.value).endOf('day').toISOString())
                  }}
                />
              </Grid>
            </Grid>
            <Button
              vatiant='text'
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
              }}
            >
              Clear Date Filter
            </Button>
            <Button 
              variant='contained'
              onClick={onClick('sessions', {startDate, endDate})}
            >
              Create Session Export
            </Button>
          </>
        }
        {exportType === 'patients' &&
          <Button 
            variant='contained'
            onClick={onClick('patients')}
          >
            Create Patient Export
          </Button>
        }
        {exportType === 'providers' &&
          <Button 
            variant='contained'
            onClick={onClick('providers')}
          >
            Create Provider Export
          </Button>
        }
        {!!exportType && <Button
          color='success'
          variant='contained'
          href={downloadUrl}
          disabled={!downloadUrl || !downloadReady}
        >
          Download Export
        </Button>
        }
      </Stack>
    </Box>
  );
};

export default DataExportScreen;